import axios from 'axios';
import {
	API_ENDPOINTS,
	Button,
	ButtonTypes,
	Card,
	CardBody,
	CardData,
	customToast,
	DateFormats,
	Dropdown,
	formatDateFns,
	FormInput,
	getThemeFromUrl,
	Icons,
	Modal,
	PageLayout,
	Permissions,
	replaceKeyWithValue,
	Roles,
	route,
	SelectOption,
	Spinner,
	StringHelpers,
	SubTitle,
	Themes,
	useAuthorisationContext,
	useDisabledContext,
	useForm,
	useLangContext,
	useQuery,
	Variants,
	WidthConstrainedContainer,
} from 'carrier-fe';
import { DropdownItemProps } from 'carrier-fe/lib/components/Controls/Dropdown/DropdownItem/DropdownItem';
import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { CompanyTypes, REG_STATUS, SEARCH_FILTERS } from '../../../Constants';
import { CompanyApproval, CompanyView } from '../../../types/companies/view';
import UsersList from './PageComponents/UsersList';
import { CompanyIndex } from '../../../types/companies';
import DocumentsList from './PageComponents/DocumentsList';
import ToshibaWarrantyOverridesList from './PageComponents/ToshibaWarrantyOverridesList';
import WarrantyTemplatesList from './PageComponents/WarrantyTemplatesList';

type ModalType = 'archive' | 'restore' | 'approve' | 'reject' | null;

const defaultData: CompanyApproval = {
	distributor_ids: []
}

function Company() {
	const { companyId } = useParams();
	const navigate = useNavigate();
	const { crud, fields } = useLangContext();
	const { hasPermissionAccess, hasRoleAccess } = useAuthorisationContext();
	const { disabled, setDisabled } = useDisabledContext();
	const [activeModal, setActiveModal] = useState<ModalType>(null);
	const [distributorSelects, setDistributorSelects] = useState<SelectOption[]>();
	const [similarCompanyNames, setSimilarCompanyNames] = useState<CompanyIndex[]>();

	const openModal = (modalType: ModalType) => setActiveModal(modalType);
	const closeModal = () => setActiveModal(null);

	const {
		data: companyData,
		isLoading,
		error,
		refetch,
	} = useQuery<{ data: CompanyView }>(
		route(API_ENDPOINTS.ADMIN.COMPANY.VIEW, {
			companyId: String(companyId),
		}),
		{
			method: 'GET',
		}
	);

	useEffect(() => {
		if (error) {
			customToast({
				title: (error as any)?.response?.data?.message,
				variant: Variants.Danger,
			});
		}
	}, [error]);

	const archive = async () => {
		setDisabled(true);

		try {
			const { data } = await axios.delete(
				route(API_ENDPOINTS.ADMIN.COMPANY.DELETE, {
					companyId: String(companyId),
				})
			);
			customToast({
				title: data.message || '',
				variant: Variants.Success,
			});
			refetch();
		} catch (err: any) {
			customToast({
				title:
					err?.response?.data?.message ||
					'An error occurred. Please try again.',
				variant: Variants.Danger,
			});
		} finally {
			setDisabled(false);
			closeModal();
		}
	};

	const restore = async () => {
		setDisabled(true);

		try {
			let response = await axios.patch(
				route(API_ENDPOINTS.ADMIN.COMPANY.RESTORE, {
					companyId: String(companyId),
				})
			);
			customToast({
				title: response?.data?.message || 'Success.',
				variant: Variants.Success,
			});
			refetch();
		} catch (error: any) {
			customToast({
				title:
					error?.response?.data?.message ||
					'An error occurred. Please try again.',
				variant: Variants.Danger,
			});
		} finally {
			setDisabled(false);
			closeModal();
		}
	};

	const fetchDistributors = async () => {
		setDisabled(true);

		try {
			const response = await axios.get(
				route(API_ENDPOINTS.GENERIC.SELECT.COMPANIES + `?types[]=${CompanyTypes.DISTRIBUTOR}`)
			);
		
			setDistributorSelects(response?.data?.data);
		} catch (error: any) {
			customToast({
				title:
					error?.response?.data?.message ||
					'Error loading distributors',
				variant: Variants.Danger,
			});
		} finally {
			setDisabled(false);
		}
	};

	const fetchSimilarCompanyNames = async () => {
		setDisabled(true);

		try {
			const response = await axios.get(
				route(API_ENDPOINTS.ADMIN.COMPANY.INDEX + `?filter[search]=${companyData?.data?.name}`),
			);

			const withoutCurrentCompany = 
				response?.data?.data?.rows?.filter(
					(company: CompanyIndex) => company.id !== companyId
				);

			setSimilarCompanyNames(withoutCurrentCompany);
		} catch (error: any) {
			customToast({
				title:
					error?.response?.data?.message ||
					'Error fetching similar company names',
				variant: Variants.Danger,
			});
		} finally {
			setDisabled(false);
		}
	};

	const submitApproval = async (data: CompanyApproval) => {
		return axios.post(
			route(
				API_ENDPOINTS.ADMIN.COMPANY.APPROVE,
				{ companyId: companyId || '' }
			), 
			data
		);
	};

	const reject = async () => {
		setDisabled(true);

		try {
			const response = await axios.post(
				route(
					API_ENDPOINTS.ADMIN.COMPANY.REJECT,
					{ companyId: companyId || '' }
				)
			);
			customToast({
				title: response?.data?.message || 'Success.',
				variant: Variants.Success,
			});
			refetch();
		} catch (error: any) {
			customToast({
				title:
					error?.response?.data?.message ||
					'Error rejecting company registration',
				variant: Variants.Danger,
			});
		} finally {
			setDisabled(false);
			closeModal();
		}
	};

	const { 
		store, 
		setValues, 
		Submit, 
		isSuccess, 
		errors, 
		setErrors 
	} = useForm(submitApproval, defaultData);

	useEffect(() => {
		if (isSuccess) {
			refetch();
			closeModal();
		}
	}, [isSuccess]);

	const getActions = (): DropdownItemProps[] => {
		if (!companyData || !companyData?.data) return [];
		const actions: DropdownItemProps[] = [];

		const { permissions } = companyData?.data;

		if (
			hasPermissionAccess(
				Permissions.ADMIN_COMPANY_APPROVE_REGISTRATION
			) &&
			companyData?.data?.registration_status === REG_STATUS.PENDING &&
			!companyData?.data?.deleted_at &&
			themes.isToshiba
		) {
			actions.push({
				label: StringHelpers.title(
					fields?.approve_registration || 'Approve registration' //ADD LANG
				),
				onClick: () => {
					if (!distributorSelects) {
						fetchDistributors();
					}
					if (!similarCompanyNames) {
						fetchSimilarCompanyNames();
					}
					const companyDistributorIds = 
						companyData?.data?.distributors?.map((distributor) => {
						return distributor.id
					});
					setValues({
						distributor_ids: [
							...store?.distributor_ids, 
							...companyDistributorIds || []
						]
					});
					openModal('approve');
				},
				variant: Variants.Dark,
				icon: Icons.SUCCESS,
				disabled: disabled,
			});
		}

		if (
			hasPermissionAccess(
				Permissions.ADMIN_COMPANY_REJECT_REGISTRATION
			) &&
			companyData?.data?.registration_status === REG_STATUS.PENDING &&
			!companyData?.data?.deleted_at &&
			themes.isToshiba
		) {
			actions.push({
				label: StringHelpers.title(
					fields?.reject_registration || 'Reject registration' //ADD LANG
				),
				onClick: () => openModal('reject'),
				variant: Variants.Dark,
				icon: Icons.WARNING,
				disabled: disabled,
			});
		}

		if (permissions.update) {
			actions.push({
				label: StringHelpers.title(
					crud?.buttons?.edit?.default || 'Edit'
				),
				onClick: () => navigate(`/companies/${companyId}/edit`),
				variant: Variants.Dark,
				icon: Icons.EDIT,
				disabled: disabled,
			});
		}

		if (permissions.delete && !companyData?.data?.deleted_at) {
			actions.push({
				label: StringHelpers.title(
					crud?.buttons.archive.default || 'Archive'
				),
				onClick: () => openModal('archive'),
				variant: Variants.Danger,
				icon: Icons.DELETE,
				disabled: disabled,
			});
		}

		if (permissions.restore && companyData?.data?.deleted_at) {
			actions.push({
				label: StringHelpers.title(
					crud?.buttons.restore.default || 'Restore'
				),
				onClick: () => openModal('restore'),
				variant: Variants.Dark,
				icon: Icons.CHECKBOX_ON,
				disabled: disabled,
			});
		}

		return actions;
	};

	const themes = useMemo(() => {
		return {
			isToshiba: getThemeFromUrl() === Themes.Toshiba,
			isViessmann: getThemeFromUrl() === Themes.Viessmann,
		};
	}, []);

	const booleanOptions = useMemo(() => {
		return {
			YES: crud?.options?.boolean?.find((x: any) => x.value === '1')
				?.label,
			NO: crud?.options?.boolean?.find((x: any) => x.value === '0')
				?.label,
		};
	}, [crud?.options?.boolean]);

	const title = replaceKeyWithValue(
		crud?.pages?.view.title || 'View :model',
		'model',
		crud?.models?.company || 'Company'
	);

	const actions = getActions();

	const currentYear = (): number => {
		return new Date().getFullYear();
	};

	const installations = companyData?.data?.installation_numbers || [];

	const installationFigures = {
		thisYear: installations.find((installation) => {
			return installation.year === currentYear();
		}),

		lastYear: installations.find((installation) => {
			return installation.year === currentYear() - 1;
		})
	};

	const isSuperAdminOrPlatformManager = 
		hasRoleAccess(Roles.PLATFORM_MANAGER) || 
		hasRoleAccess(Roles.SUPER_ADMIN);

	return (
		<PageLayout title={StringHelpers.title(title)}>
			<WidthConstrainedContainer>
				{isLoading ? (
					<div className="d-flex justify-content-center align-items-center vh-100">
						<Spinner />
					</div>
				) : (
					<>
						<div className="mb-4 d-flex justify-content-between align-items-center">
							<Button
								label={StringHelpers.title(
									crud?.buttons.back.default || 'Back'
								)}
								onClick={() => {
									const savedFilter = sessionStorage.getItem(SEARCH_FILTERS.COMPANY);
									sessionStorage.removeItem(SEARCH_FILTERS.COMPANY);
									navigate(
										savedFilter
											? `/companies?filter[search]=${encodeURIComponent(savedFilter)}`
											: '/companies'
									);
								}}
								variant={Variants.Dark}
								type={ButtonTypes.Outline}
								disabled={disabled}
							/>
							{actions.length > 0 && (
								<Dropdown
									label={StringHelpers.title(
										crud?.buttons?.actions?.default ||
											'Actions'
									)}
									items={getActions()}
									type={ButtonTypes.Solid}
									variant={Variants.Dark}
									disabled={disabled}
								/>
							)}
						</div>
						<Card>
							<CardBody>
								<SubTitle
									title={StringHelpers.title(
										`${crud?.models?.company} ${crud?.sub_titles?.details}`
									)}
									className="mt-4"
									style={{ color: '#464C5E' }}
								/>
								<div className="row">
									<CardData.Column>
										<CardData.ColumnItem
											title={StringHelpers.title(
												fields?.name || 'Name'
											)}
											value={
												companyData?.data?.name || '-'
											}
										/>
										<CardData.ColumnItem
											title={StringHelpers.title(
												fields?.type || 'Type'
											)}
											value={StringHelpers.title(
												companyData?.data?.type || '-'
											)}
										/>
										<CardData.ColumnItem
											title={StringHelpers.title(
												fields?.registration_state ||
													'Registration state'
											)}
											value={StringHelpers.title(
												companyData?.data
													?.registration_status || '-'
											)}
										/>
									</CardData.Column>
									<CardData.Column>
										<CardData.ColumnItem
											title={StringHelpers.title(
												fields?.customer_email_enabled ||
													'Customer email enabled'
											)}
											value={
												companyData?.data
													?.customer_email_enabled
													? booleanOptions.YES
													: booleanOptions.NO
											}
										/>
										<CardData.ColumnItem
											title={StringHelpers.title(
												fields?.system_access_enabled ||
													'System access enabled'
											)}
											value={
												companyData?.data
													?.system_access_enabled
													? booleanOptions.YES
													: booleanOptions.NO
											}
										/>
										<CardData.ColumnItem
											title={StringHelpers.title(
												`${crud?.sub_titles?.primary} ${crud?.models?.user}`
											)}
											value={
												<>
													{companyData?.data
														?.primary_individual
														?.user?.permissions
														?.view ? (
														<a
															href={`/user/${companyData?.data?.primary_individual?.user?.id}`}
															target="_blank"
															rel="noopener noreferrer"
														>
															{StringHelpers.title(
																companyData
																	?.data
																	?.primary_individual
																	?.name ||
																	'-'
															)}
														</a>
													) : (
														StringHelpers.title(
															companyData?.data
																?.primary_individual
																?.name || '-'
														)
													)}
												</>
											}
										/>
									</CardData.Column>
									{companyData?.data?.type === CompanyTypes.INSTALLER &&
										themes.isToshiba && (
											<CardData.Column>
												<CardData.ColumnItem
													title={StringHelpers.title(
														fields?.distributors ||
															'Distributors'
													)}
													value={
														<>
															{companyData?.data?.distributors?.length? 
																<ul className='list-unstyled'>
																	{companyData?.data?.distributors?.map((distributor) => (
																		<li key={distributor.id}>
																			<a
																				href={`/companies/${distributor.id}`}
																				key={
																					distributor.id
																				}
																				target="_blank"
																				rel="noopener noreferrer"
																			>
																				{distributor.name}
																			</a>
																		</li>
																	))}
																</ul> 
																: '-'
															}
														</>
													}
												/>
												<CardData.ColumnItem
													title={StringHelpers.title(
														fields?.warranty_level ||
															'Warranty Level'
													)}
													value={
														companyData?.data
															?.toshiba_warranty_level?.name ||
														'-'
													}
												/>
											</CardData.Column>
									)}
									<CardData.Column>
										<CardData.ColumnItem
											title={StringHelpers.title(
												fields?.created_at || 'Created'
											)}
											value={formatDateFns(
												companyData?.data?.created_at ||
													'',
												DateFormats.DATETIME_SHORT
											)}
										/>
										<CardData.ColumnItem
											title={StringHelpers.title(
												fields?.updated_at || 'Updated'
											)}
											value={formatDateFns(
												companyData?.data?.updated_at ||
													'',
												DateFormats.DATETIME_SHORT
											)}
										/>
										{!!companyData?.data?.deleted_at && (
											<CardData.ColumnItem
												title={StringHelpers.title(
													fields?.archived_at ||
														'Archived'
												)}
												value={formatDateFns(
													companyData?.data
														?.deleted_at,
													DateFormats.DATETIME_SHORT
												)}
											/>
										)}
									</CardData.Column>
								</div>
								<SubTitle
									className="mt-4"
									style={{ color: '#464C5E' }}
									title={StringHelpers.title(
										`${crud?.sub_titles?.address}`
									)}
								/>
								<div className="row">
									<CardData.Column>
										<CardData.ColumnItem
											title={StringHelpers.title(
												`${fields?.address_line_1}`
											)}
											value={
												companyData?.data?.primary_site
													?.address?.address_line_1 ||
												'-'
											}
										/>
										<CardData.ColumnItem
											title={StringHelpers.title(
												`${fields?.address_line_2}`
											)}
											value={
												companyData?.data?.primary_site
													?.address?.address_line_2 ||
												'-'
											}
										/>
										<CardData.ColumnItem
											title={StringHelpers.title(
												`${fields?.address_line_3}`
											)}
											value={
												companyData?.data?.primary_site
													?.address?.address_line_3 ||
												'-'
											}
										/>
									</CardData.Column>
									<CardData.Column>
										<CardData.ColumnItem
											title={StringHelpers.title(
												`${fields?.town_city}`
											)}
											value={
												companyData?.data?.primary_site
													?.address?.town_city || '-'
											}
										/>
										<CardData.ColumnItem
											title={StringHelpers.title(
												`${fields?.state_county}`
											)}
											value={
												companyData?.data?.primary_site
													?.address?.state_county ||
												'-'
											}
										/>
									</CardData.Column>
									<CardData.Column>
										<CardData.ColumnItem
											title={StringHelpers.title(
												fields?.postcode_zipcode ||
													'Postcode / Zipcode'
											)}
											value={StringHelpers.upper(
												companyData?.data?.primary_site
													?.address
													?.postcode_zipcode || '-'
											)}
										/>
										<CardData.ColumnItem
											title={StringHelpers.title(
												`${fields?.country_code_iso_3}`
											)}
											value={
												companyData?.data?.primary_site
													?.address?.country || '-'
											}
										/>
									</CardData.Column>
								</div>
								<>
									<SubTitle
										className="mt-4"
										style={{ color: '#464C5E' }}
										title={StringHelpers.title(
											`${crud?.sub_titles?.additional_platform} ${crud?.sub_titles?.details}`
										)}
									/>
									<div className="row">
										{themes.isViessmann && (
											<>
												<CardData.Column>
													<CardData.ColumnItem
														title={StringHelpers.title(
															fields?.gassafe_number ||
																'Gas Safe number'
														)}
														value={
															companyData?.data
																?.gas_safe_number ||
															'-'
														}
													/>
													<CardData.ColumnItem
														title={StringHelpers.title(
															fields?.gassafe_confirmed_At ||
																'Gas Safe confirmed at'
														)}
														value={formatDateFns(
															companyData?.data
																?.gas_safe_confirmed_at ||
																'-',
															DateFormats.DATETIME_SHORT
														)}
													/>
												</CardData.Column>

												<CardData.Column>
													<CardData.ColumnItem
														title={StringHelpers.title(
															fields?.sap_number ||
																'SAP number'
														)}
														value={
															companyData
																?.data
																?.sap_number ||
															'-'
														}
													/>

													<CardData.ColumnItem
														title={StringHelpers.title(
															fields?.mcs_number ||
																'MCS number'
														)}
														value={
															companyData
																?.data
																?.mcs_number ||
															'-'
														}
													/>
												</CardData.Column>
												
												<CardData.Column>
													<CardData.ColumnItem
														title={StringHelpers.title(
															`${fields?.number_of_registrations} ${currentYear()}`
														)}
														value={
															installationFigures.thisYear?.count
														}
													/>

													<CardData.ColumnItem
														title={StringHelpers.title(
															`${fields?.number_of_registrations} ${currentYear() - 1}`
														)}
														value={
															installationFigures.lastYear?.count
														}
													/>
												</CardData.Column>
												
												<CardData.Column>
													<CardData.ColumnItem
														title={fields?.vipartner || 'VIPartner'}
														value={companyData?.data.vipartner ? 'Yes' : 'No'}
													/>

													<CardData.ColumnItem
														title={fields?.vipartner_starts_at || 'VIPartner Starts At'}
														value={formatDateFns(
															companyData?.data.vipartner_starts_at || '-',
															DateFormats.DATE
														)}
													/>

													<CardData.ColumnItem
														title={fields?.vipartner_ends_at || 'VIPartner Ends At (Exclusive)'}
														value={formatDateFns(
															companyData?.data.vipartner_ends_at || '-',
															DateFormats.DATE
														)}
													/>
												</CardData.Column>
											</>
										)}
										{themes.isToshiba && companyData?.data?.type === CompanyTypes.INSTALLER && (
											<CardData.Column>
												<CardData.ColumnItem
													title={fields?.fgas_refcom_standard_number 
														|| 'FGAS/REFCOM standard number'
													}
													value={companyData
														?.data
														?.fgas_refcom_standard_number
														|| '-'
													}
												/>
												<CardData.ColumnItem
													title={StringHelpers.title(
														fields?.account_number 
														|| 'account number'
													)}
													value={companyData
														?.data
														?.account_number
														|| '-'
													}
												/>
											</CardData.Column>
										)}
									</div>
								</>
							</CardBody>
						</Card>
						{companyData &&
							<UsersList 
								companyRegStatus={
									companyData?.data?.registration_status
								} 
							/>
						}
					{themes.isToshiba && <DocumentsList />}
					{themes.isToshiba 
						&& companyData?.data?.type === CompanyTypes.INSTALLER 
						&& (
							<ToshibaWarrantyOverridesList 
								companyName={companyData?.data?.name} 
								onChange={refetch}
							/>
						)
					}
					{themes.isToshiba
						&& companyData?.data?.type === CompanyTypes.DISTRIBUTOR
						&& companyData?.data?.warranty_templates
						&& (
							<WarrantyTemplatesList
								companyId={String(companyId)}
								templates={companyData.data.warranty_templates}
								onChange={refetch}
							/>
						)
					}
				</>
			)}
		</WidthConstrainedContainer>

			<Modal
				open={activeModal === 'archive'}
				onClose={closeModal}
				title={StringHelpers.title(
					replaceKeyWithValue(
						crud?.modals?.archive?.title || 'Archive :name?',
						'name',
						crud?.models?.company || 'Company'
					)
				)}
				confirmText={
					disabled
						? crud?.buttons?.archive?.submitting || 'Archiving...'
						: crud?.buttons?.archive?.default || 'Archive'
				}
				closeText={crud?.buttons?.cancel?.default || 'Cancel'}
				onConfirm={archive}
				disabled={disabled}
				variant={Variants.Danger}
			>
				{crud?.modals?.archive?.description?.map(
					(item: string, key: number) => {
						return (
							<p key={key}>
								{replaceKeyWithValue(
									item,
									'name',
									companyData?.data?.name || '-'
								)}
							</p>
						);
					}
				)}
			</Modal>

			<Modal
				open={activeModal === 'restore'}
				onClose={closeModal}
				title={StringHelpers.title(
					replaceKeyWithValue(
						crud?.modals?.restore?.title || 'Restore :name?',
						'name',
						crud?.models?.company || 'Company'
					)
				)}
				confirmText={
					disabled
						? crud?.buttons?.restore?.submitting || 'Restoring...'
						: crud?.buttons?.restore?.default || 'Restore'
				}
				closeText={crud?.buttons?.cancel?.default || 'Cancel'}
				onConfirm={restore}
				disabled={disabled}
				variant={Variants.Success}
			>
				{crud?.modals?.restore?.description?.map(
					(item: string, key: number) => {
						return (
							<p key={key}>
								{replaceKeyWithValue(
									item,
									'name',
									companyData?.data?.name || '-'
								)}
							</p>
						);
					}
				)}
			</Modal>

			<Modal
				open={activeModal === 'approve'}
				onClose={() => {
					setErrors({});
					closeModal();
				}}
				title={StringHelpers.title(
					replaceKeyWithValue(
						crud?.modals?.approve?.title || 'Approve :name?',
						'name',
						crud?.models?.company || 'Company'
					)
				)}
				closeText={crud?.buttons?.cancel?.default || 'Cancel'}
				disabled={disabled}
				variant={Variants.Success}
				overriddenConfirmButton={
					<Submit
						defaultLabel={
							crud?.buttons?.approve?.default || 'Approve'
						}
						loadingLabel={
							crud?.buttons?.approve?.submitting || 'Approving...'
						}
						variant={ Variants.Success }
						disabled={ disabled }
					/>
				}
			>
				{crud?.modals?.approve?.description?.map(
					(item: string, key: number) => {
						return (
							<p key={key}>
								{replaceKeyWithValue(
									item,
									'name',
									companyData?.data?.name || '-'
								)}
							</p>
						);
					}
				)}

				{similarCompanyNames && 
					similarCompanyNames.length >= 1 &&
					<div className='mb-4'>
						<p>
							{fields?.similar_company_names 
								|| 'Here are some other companies with a similar name:'
							}
						</p>
						<ul>
							{similarCompanyNames.map((company) => {
								return (
									<li key={company.id}>
										<a
											href={`/companies/${company.id}`}
											target='_blank'
											rel='noopener noreferrer'
										>
											{company.name}
										</a>
									</li>
								)
							})}
						</ul>
					</div>
				}

				{<FormInput
					type='searchable-select'
					name='distributor_ids'
					label={StringHelpers.title(
						fields?.distributor_ids || 'Distributors'
					)}
					placeholder={
						fields?.notify_distributors
						|| "Please select which distributors you'd like to notify"
					}
					value={store?.distributor_ids}
					onChange={(v) =>
						setValues({
							distributor_ids: v as any,
						})
					}
					isMulti
					options={distributorSelects}
					disabled={
						disabled 
						|| !isSuperAdminOrPlatformManager
					}
					errorMessages={errors?.distributor_ids}
				/>}
			</Modal>

			<Modal
				open={activeModal === 'reject'}
				onClose={closeModal}
				title={StringHelpers.title(
					replaceKeyWithValue(
						crud?.modals?.reject?.title || 'Reject :name?',
						'name',
						crud?.models?.company || 'Company'
					)
				)}
				confirmText={
					disabled
						? crud?.buttons?.reject?.submitting || 'Rejecting...'
						: crud?.buttons?.reject?.default || 'Reject'
				}
				closeText={crud?.buttons?.cancel?.default || 'Cancel'}
				onConfirm={reject}
				disabled={disabled}
				variant={Variants.Success}
			>
				{crud?.modals?.reject?.description?.map(
					(item: string, key: number) => {
						return (
							<p key={key}>
								{replaceKeyWithValue(
									item,
									'name',
									companyData?.data?.name || '-'
								)}
							</p>
						);
					}
				)}
			</Modal>
		</PageLayout>
	);
}

export default Company;
